import { isRoutePath } from './sign-in-hop'

/**
 * This tab's last sign-in failure, so a reload of the callback that shows it still has the details
 * for support. Forgotten when the person leaves the callback, starts somewhere else, or signs out.
 */
const failureKey = 'tms.auth.signInFailure'

// Moves on with every keep or forget, so a sign-out can tell its failure was dismissed or replaced meanwhile.
let changes = 0

export function keepSignInFailure(failure: unknown): void {
  changes += 1
  try {
    sessionStorage.setItem(failureKey, JSON.stringify(failure))
  } catch {
    // Storage blocked: the failure is shown, only not again after a reload.
  }
}

/** What {@link keepSignInFailure} kept, unchecked; `null` with none. */
export function readKeptSignInFailure(): unknown {
  try {
    const value = sessionStorage.getItem(failureKey)
    return value === null ? null : JSON.parse(value)
  } catch {
    return null
  }
}

export function forgetSignInFailure(): void {
  changes += 1
  try {
    sessionStorage.removeItem(failureKey)
  } catch {
    // Nothing kept.
  }
}

/** The failure the sign-outs under way forgot, shared so overlapping ones put it back once. */
let signingOut: { readonly kept: unknown; readonly forgotten: number; running: number; hasLeft: boolean } | null = null

/**
 * Runs a sign-out with the kept failure forgotten, as sign-out comes back through the callback,
 * which would show it again. If every sign-out under way throws, the failure is kept again, but
 * only while the failure page still shows that failure: one dismissed or replaced meanwhile stays gone.
 */
export async function signOutForgettingFailure(redirectPath: string, signOut: () => Promise<void>): Promise<void> {
  if (signingOut === null) {
    const kept = readKeptSignInFailure()
    forgetSignInFailure()
    signingOut = { kept, forgotten: changes, running: 0, hasLeft: false }
  }
  const attempt = signingOut
  attempt.running += 1
  try {
    await signOut()
    attempt.hasLeft = true
  } catch (error: unknown) {
    if (
      attempt.running === 1
      && !attempt.hasLeft
      && attempt.kept !== null
      && changes === attempt.forgotten
      && isRoutePath(redirectPath, window.location.pathname)
    ) {
      keepSignInFailure(attempt.kept)
    }
    throw error
  } finally {
    attempt.running -= 1
    if (attempt.running === 0 && signingOut === attempt) {
      signingOut = null
    }
  }
}
